import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, Search, ChevronDown, ChevronUp, Trophy } from "lucide-react";
import { getEntriesByUserId, GameEntry, PageResponse } from "@/lib/gameApi";
import BottomNav from "@/components/BottomNav";

const BetHistory = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Accumulated entries
  const [entries, setEntries] = useState<GameEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<GameEntry[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);

  // UI state
  const [historyLoading, setHistoryLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showOnlyWins, setShowOnlyWins] = useState(false);

  // Redirect unauthenticated or admin users
  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }
      if (user.role === "ADMIN") {
        navigate("/admin", { replace: true });
      }
    }
  }, [user, loading, navigate]);

  const hasFetched = useRef(false);

  // Load first page (or reset when user changes)
  const loadFirstPage = async () => {
    if (!user) return;
    try {
      setHistoryLoading(true);
      const response: PageResponse<GameEntry> = await getEntriesByUserId(user.id, 0, 20);
      const content = response.content || [];
      setEntries(content);
      setTotalElements(response.totalElements);
      setHasMore(!response.last);
      setCurrentPage(0);
    } catch (error) {
      console.error("Failed to load bet history:", error);
      setEntries([]);
      setTotalElements(0);
      setHasMore(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load next page and append
  const loadMore = async () => {
    if (!user || loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const response: PageResponse<GameEntry> = await getEntriesByUserId(user.id, nextPage, 20);
      const newEntries = response.content || [];
      setEntries((prev) => [...prev, ...newEntries]);
      setHasMore(!response.last);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Failed to load more entries:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (!user || hasFetched.current) return;
    hasFetched.current = true;
    loadFirstPage();
  }, [user]);

  // Apply filters & sorting whenever entries, search, sort order, or win toggle change
  useEffect(() => {
    let filtered = [...entries];

    if (showOnlyWins) {
      filtered = filtered.filter((e) => e.result === "win");
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.playerName?.toLowerCase().includes(query) ||
          e.gameName?.toLowerCase().includes(query) ||
          e.gameType?.toLowerCase().includes(query) ||
          e.playType?.toLowerCase().includes(query) ||
          e.number?.includes(query)
      );
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt || "").getTime();
      const dateB = new Date(b.createdAt || "").getTime();
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

    setFilteredEntries(filtered);
  }, [entries, searchQuery, sortOrder, showOnlyWins]);

  const stats = {
    totalBets: entries.length,
    totalAmount: entries.reduce((sum, e) => sum + (e.amount || 0), 0),
    totalWins: entries.filter((e) => e.result === "win").length,
    totalWinAmount: entries
      .filter((e) => e.result === "win")
      .reduce((sum, e) => sum + (e.winAmount || 0), 0),
    totalLosses: entries.filter((e) => e.result === "lose").length,
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return "Today";
    } else if (d.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  };

  if (loading || !user || historyLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-3 py-2 sticky top-0 z-10 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-white" />
            <h1 className="text-sm font-semibold text-white">Bet History</h1>
          </div>
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="px-3 mt-2">
        <button
          onClick={() => setShowOnlyWins(!showOnlyWins)}
          className={`w-full py-1.5 rounded-lg font-semibold text-xs transition-all duration-200 ${
            showOnlyWins
              ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-md"
              : "bg-white border border-gray-200 text-gray-700 hover:border-yellow-400"
          }`}
        >
          {showOnlyWins ? "🏆 Showing Only Wins" : "👁️ Show All Bets"}
        </button>
      </div>

      {/* Search */}
      <div className="px-3 mt-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by player, game, or number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border border-gray-300 pl-8 pr-3 py-1.5 text-xs rounded-lg focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Sort */}
      <div className="px-3 mt-1.5 flex justify-end">
        <button
          onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
          className="flex items-center gap-0.5 text-[10px] text-gray-600 hover:text-gray-900"
        >
          {sortOrder === "desc" ? "Newest First" : "Oldest First"}
          {sortOrder === "desc" ? (
            <ChevronDown className="w-2.5 h-2.5" />
          ) : (
            <ChevronUp className="w-2.5 h-2.5" />
          )}
        </button>
      </div>

      {/* History List */}
      <div className="px-3 mt-2 space-y-2 pb-6">
        {filteredEntries.length === 0 ? (
          <div className="text-center py-10">
            <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2 shadow-md">
              <Clock className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-sm text-gray-500 font-medium">No bets found</p>
            <p className="text-[10px] text-gray-400 mt-0.5">
              Place your first bet to see history
            </p>
          </div>
        ) : (
          <>
            {filteredEntries.map((e, index) => {
              const isWin = e.result === "win";
              return (
                <div
                  key={e.id ?? index}
                  className={`bg-white rounded-lg border-2 p-3 shadow-sm transition-all duration-300 hover:shadow-md ${
                    isWin
                      ? "border-green-200 hover:border-green-400"
                      : e.result === "lose"
                      ? "border-red-100 hover:border-red-200"
                      : "border-yellow-100 hover:border-yellow-200"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {/* Game Info */}
                      <div className="flex items-center flex-wrap gap-1.5 mb-2">
                        {isWin && (
                          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <Trophy className="w-2 h-2" />
                            WINNER
                          </div>
                        )}
                        <span className="text-[10px] font-medium text-gray-400">
                          #{index + 1}
                        </span>
                        <span className="text-xs font-bold text-gray-900">
                          {e.gameName}
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 rounded-full text-gray-600">
                          {e.gameType}
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-blue-100 rounded-full text-blue-600 font-semibold">
                          {e.playType}
                        </span>
                      </div>

                      {/* Number */}
                      <div className="mb-2">
                        <div
                          className={`inline-flex items-center justify-center px-3 py-1 rounded-md ${
                            isWin ? "bg-gradient-to-r from-green-50 to-emerald-50" : "bg-gray-50"
                          }`}
                        >
                          <span
                            className={`text-lg font-black ${
                              isWin ? "text-green-600" : "text-blue-600"
                            }`}
                          >
                            {e.number}
                          </span>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex flex-wrap gap-2 text-[10px] text-gray-500">
                        <div className="flex items-center gap-0.5">
                          <span>👤</span>
                          <span className="font-medium text-gray-700">{e.playerName}</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <span>💰</span>
                          <span className="font-medium text-gray-700">₹{e.amount}</span>
                        </div>
                        {isWin && (
                          <div className="flex items-center gap-0.5">
                            <span>🎉</span>
                            <span className="font-bold text-green-600">+₹{e.winAmount}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-0.5">
                          <span>📅</span>
                          <span className="font-medium text-gray-700">
                            {formatDate(e.createdAt || "")}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <span>⏰</span>
                          <span className="font-medium text-gray-700">
                            {new Date(e.createdAt || "").toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Result Badge */}
                    <div className="ml-3">
                      {e.result === "win" && (
                        <div className="bg-gradient-to-br from-green-500 to-emerald-600 px-2 py-1 rounded-lg text-center shadow-md">
                          <p className="text-[8px] font-mono text-green-100">WIN</p>
                          <p className="text-[10px] font-black text-white">+₹{e.winAmount}</p>
                        </div>
                      )}
                      {e.result === "lose" && (
                        <div className="bg-gradient-to-br from-red-500 to-rose-600 px-2 py-1 rounded-lg text-center shadow-md">
                          <p className="text-[8px] font-mono text-red-100">LOSS</p>
                          <p className="text-[10px] font-black text-white">-₹{e.amount}</p>
                        </div>
                      )}
                      {(!e.result || e.result === "pending") && (
                        <div className="bg-gradient-to-br from-yellow-500 to-orange-500 px-2 py-1 rounded-lg text-center shadow-md">
                          <p className="text-[8px] font-mono text-yellow-100">PENDING</p>
                          <p className="text-[10px] font-black text-white">₹{e.amount}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Load More Button */}
            {hasMore && entries.length < totalElements && (
              <div className="flex justify-center pt-2 pb-4">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-all disabled:opacity-50"
                >
                  {loadingMore ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default BetHistory;